<template>
  <div class="home" v-loading="loading">
    <div class="toolbar">
      <div class="title">经营看板</div>
      <el-radio-group v-model="range" size="default" @change="loadOverview">
        <el-radio-button label="TODAY">今日</el-radio-button>
        <el-radio-button label="D7">近7日</el-radio-button>
        <el-radio-button label="D30">近30日</el-radio-button>
      </el-radio-group>
    </div>
    <div class="period-hint" v-if="overview">
      统计区间：{{ formatRange(overview.currentStart, overview.currentEnd) }}
      · 对比上期：{{ formatRange(overview.previousStart, overview.previousEnd) }}
    </div>

    <div class="metric-row">
      <div class="metric-card" v-for="item in primaryMetrics" :key="item.key">
        <p class="metric-title">{{ item.title }}</p>
        <p class="metric-value">{{ item.value }}</p>
        <p class="metric-mom" :class="momClass(item.momRate)">
          环比 {{ formatMom(item.momRate) }}
          <span v-if="item.prevLabel" class="prev">上期 {{ item.prevLabel }}</span>
        </p>
      </div>
    </div>

    <div class="metric-row secondary">
      <div class="metric-card" v-for="item in secondaryMetrics" :key="item.key" :class="{ clickable: item.clickable }" @click="item.onClick && item.onClick()">
        <p class="metric-title">{{ item.title }}</p>
        <p class="metric-value">{{ item.value }}</p>
        <p class="metric-sub" v-if="item.sub">{{ item.sub }}</p>
        <p class="metric-mom" v-if="item.showMom" :class="momClass(item.momRate)">
          环比 {{ formatMom(item.momRate) }}
        </p>
      </div>
    </div>

    <div class="province-section">
      <div class="province-head">
        <div class="title">订单来源（按小程序平台）</div>
      </div>
      <el-table :data="platformStats" size="small" stripe empty-text="暂无平台订单数据" max-height="260">
        <el-table-column prop="platformName" label="平台" min-width="100" />
        <el-table-column prop="orderCount" label="成交订单" width="120" />
        <el-table-column prop="revenue" label="成交额" width="120" />
      </el-table>
    </div>

    <div class="province-section">
      <div class="province-head">
        <div class="title">订单来源（按省）</div>
        <el-button link type="primary" @click="provinceAllVisible = true">全部</el-button>
      </div>
      <el-table :data="provinceTop5" size="small" stripe empty-text="暂无省份订单数据" max-height="260">
        <el-table-column type="index" label="排名" width="70" />
        <el-table-column prop="provinceName" label="省份" min-width="120" />
        <el-table-column prop="orderCount" label="订单数" width="100" />
      </el-table>
    </div>

    <el-dialog v-model="provinceAllVisible" title="全部省份订单来源" width="520px">
      <el-table :data="provinceAll" size="small" stripe max-height="480" empty-text="暂无数据">
        <el-table-column type="index" label="排名" width="70" />
        <el-table-column prop="provinceName" label="省份" min-width="140" />
        <el-table-column prop="orderCount" label="订单数" width="120" sortable />
      </el-table>
    </el-dialog>

    <div class="title section-title">待办事项</div>
    <div class="user_thing">
      <div class="title">
        库存预警
        <el-table :data="inventoryList" max-height="250" :row-style="rowStyle" stripe empty-text="暂无库存预警数据" color="#2c3e50">
          <el-table-column fixed prop="id" label="商品ID" />
          <el-table-column prop="merchandiseName" label="商品名称" />
          <el-table-column prop="image" label="商品图片">
            <template #default="scope">
              <img v-if="scope.row.image" :src="scope.row.image" style="width: 50px; height: 50px;">
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="stock" label="商品库存" />
        </el-table>
      </div>
      <div class="title">
        待审核作品
        <el-table :data="reviewList" max-height="250" stripe empty-text="暂无待审核作品" color="#2c3e50">
          <el-table-column fixed prop="id" label="作品ID" width="100" />
          <el-table-column prop="photoName" label="作品名称" min-width="120" show-overflow-tooltip />
          <el-table-column prop="imageUrl" label="作品图片" width="90">
            <template #default="scope">
              <img v-if="scope.row.imageUrl" :src="imageUrl(scope.row.imageUrl)" style="width: 50px; height: 50px;">
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="photoSpecification" label="规格" min-width="140" show-overflow-tooltip />
          <el-table-column prop="unitPrice" label="单价" width="80" />
        </el-table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { rqBusinessOverview, rqIndexDataView } from '@/api'

const IMAGE_HOST = 'http://xcx.ooomi.com'
const router = useRouter()
const loading = ref(false)
const range = ref('TODAY')
const overview = ref(null)
const inventoryList = ref([])
const reviewList = ref([])
const provinceAllVisible = ref(false)

const provinceAll = computed(() => overview.value?.provinceOrderStats || [])
const provinceTop5 = computed(() => provinceAll.value.slice(0, 5))
const platformStats = computed(() => overview.value?.platformOrderStats || [])

const rowStyle = () => ({ height: '50px' })

const imageUrl = (path) => {
  if (!path) return ''
  if (/^https?:\/\//.test(path)) return path
  return `${IMAGE_HOST}${path.startsWith('/') ? '' : '/'}${path}`
}

const unwrap = (res) => res?.data ?? res

const formatMoney = (v) => {
  if (v === null || v === undefined || v === '') return '0.00'
  const n = Number(v)
  if (Number.isNaN(n)) return '--'
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatCount = (v) => {
  if (v === null || v === undefined) return '0'
  return Number(v).toLocaleString('zh-CN')
}

const formatMom = (rate) => {
  if (rate === null || rate === undefined || rate === '') return '--'
  const n = Number(rate)
  if (Number.isNaN(n)) return '--'
  const pct = (n * 100).toFixed(1)
  const prefix = n > 0 ? '+' : ''
  return `${prefix}${pct}%`
}

const momClass = (rate) => {
  if (rate === null || rate === undefined || rate === '') return 'flat'
  const n = Number(rate)
  if (Number.isNaN(n) || n === 0) return 'flat'
  return n > 0 ? 'up' : 'down'
}

const formatRange = (start, end) => {
  if (!start || !end) return '--'
  const s = String(start).replace('T', ' ').slice(0, 16)
  const e = String(end).replace('T', ' ').slice(0, 16)
  return `${s} ~ ${e}`
}

const goPendingPush = () => {
  router.push({ path: '/order/orderList', query: { orderPayStatus: '1', pushStatus: '0' } })
}

const primaryMetrics = computed(() => {
  const d = overview.value || {}
  return [
    {
      key: 'orderCount',
      title: '订单数',
      value: formatCount(d.orderCount),
      momRate: d.orderCountMomRate,
      prevLabel: formatCount(d.previousOrderCount)
    },
    {
      key: 'newUser',
      title: '新用户',
      value: formatCount(d.newUserCount),
      momRate: d.newUserCountMomRate,
      prevLabel: formatCount(d.previousNewUserCount)
    },
    {
      key: 'revenue',
      title: '成交金额',
      value: `¥${formatMoney(d.revenue)}`,
      momRate: d.revenueMomRate,
      prevLabel: `¥${formatMoney(d.previousRevenue)}`
    }
  ]
})

const secondaryMetrics = computed(() => {
  const d = overview.value || {}
  const aovText = d.aov === null || d.aov === undefined ? '--' : `¥${formatMoney(d.aov)}`
  return [
    {
      key: 'aov',
      title: '客单价',
      value: aovText,
      showMom: true,
      momRate: d.aovMomRate
    },
    {
      key: 'refund',
      title: '退款金额',
      value: `¥${formatMoney(d.refundAmount)}`,
      sub: `退款单数 ${formatCount(d.refundCount)}`,
      showMom: true,
      momRate: d.refundAmountMomRate
    },
    {
      key: 'pendingPush',
      title: '待推工厂积压',
      value: formatCount(d.pendingPushCount),
      sub: '点击查看已支付未推送订单',
      clickable: true,
      onClick: goPendingPush,
      showMom: false
    }
  ]
})

const loadOverview = async () => {
  loading.value = true
  try {
    const body = unwrap(await rqBusinessOverview(range.value))
    if (body?.code && body.code !== 200) {
      ElMessage.error(body.message || '获取经营看板失败')
      return
    }
    overview.value = body?.data ?? null
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '获取经营看板失败')
  } finally {
    loading.value = false
  }
}

const loadTodo = async () => {
  try {
    const body = unwrap(await rqIndexDataView())
    if (body?.code && body.code !== 200) return
    const data = body?.data ?? {}
    inventoryList.value = (data.inventoryWarningList || []).map((item) => ({
      id: item.id,
      merchandiseName: item.merchandiseName || item.name || '-',
      image: item.merchandiseMainImage || item.image || '',
      stock: item.stock ?? item.inventory ?? '-'
    }))
    reviewList.value = data.reviewedList || []
  } catch (_) {
    // 待办为次要区块，失败不打断看板
  }
}

onMounted(() => {
  loadOverview()
  loadTodo()
})
</script>

<style lang="less" scoped>
.home {
  height: 100%;
  font-size: 16px;
  overflow: auto;
  padding-bottom: 24px;

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
  }

  .title {
    font-weight: 700;
    font-size: 18px;
  }

  .section-title {
    margin-top: 8px;
  }

  .province-section {
    margin: 8px 0 20px;
    padding: 12px 16px;
    border: 1px solid #dcdfe6;
    border-radius: 10px;
    background: #fff;
  }

  .province-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .period-hint {
    color: #666;
    font-size: 13px;
    margin-bottom: 16px;
  }

  .metric-row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;

    &.secondary .metric-card {
      min-height: 120px;
    }
  }

  .metric-card {
    flex: 1;
    box-sizing: border-box;
    padding: 16px 18px;
    border: 1px solid #dcdfe6;
    border-radius: 10px;
    background: #fff;

    &.clickable {
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;
      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
      }
    }
  }

  .metric-title {
    margin: 0 0 8px;
    color: #606266;
    font-size: 14px;
  }

  .metric-value {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #303133;
    line-height: 1.2;
  }

  .metric-sub {
    margin: 8px 0 0;
    color: #909399;
    font-size: 13px;
  }

  .metric-mom {
    margin: 10px 0 0;
    font-size: 13px;
    color: #909399;

    .prev {
      margin-left: 8px;
      color: #a8abb2;
    }

    &.up {
      color: #f56c6c;
    }
    &.down {
      color: #67c23a;
    }
    &.flat {
      color: #909399;
    }
  }

  .user_thing {
    box-sizing: border-box;
    min-height: 300px;
    width: 100%;
    padding: 12px 0 20px;
    display: flex;
    justify-content: space-between;
    gap: 16px;

    > div {
      box-sizing: border-box;
      width: 48%;
      padding: 10px;
      border: 1px solid #aaa;
      border-radius: 10px;

      :deep(.el-table) {
        margin-top: 10px;
      }
      :deep(.el-table .cell) {
        color: #2c3e50;
        padding: 0;
        img {
          width: 20px !important;
          height: 20px !important;
        }
      }
    }
  }
}
</style>
